// The hosted link player (YouTube, SoundCloud, any embed provider) as a surface: loads the item's URL into the one
// mounted player (EmbedPlayer), turns its events into MediaEvents and offers "Tap to listen" on a silent start.
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import EmbedPlayer from "../EmbedPlayer";
import type { LinkPlayerApi, LinkPlayerController, LinkPlayerEvent } from "../../lib/linkPlayer";
import type { MediaHandle } from "../media";
import type { SurfaceProps } from "../PlayableMedia";
import { ErrorOverlay, Frame, useEmit, useLatest, useTapToListen } from "./shared";
import ExternalMediaPlaceholder, { externalSiteName, useExternalMediaAllowed } from "../ExternalMediaPlaceholder";

/**
 * Privacy consent: until this browser allows external media (external_media ACCEPTED), the hosted player is not even
 * loaded — the placeholder asks first. "Load" plays this one item (nothing recorded): the next item in the dock, cast
 * or queue asks again; "Always allow" covers all.
 */
export default function EmbedSurface(p: SurfaceProps) {
  const allowed = useExternalMediaAllowed();
  const [loadedOnce, setLoadedOnce] = useState<string | null>(null);
  if (allowed || loadedOnce === p.url) return <HostedPlayerSurface {...p} />;
  const placeholder = (
    <ExternalMediaPlaceholder
      key={p.url}
      site={externalSiteName(p.kind.provider, p.url)}
      music={p.kind.id === "soundcloud"}
      onLoad={() => setLoadedOnce(p.url)}
    />
  );
  return <Frame frame={p.frame} media={placeholder} overlays={null} />;
}

function HostedPlayerSurface(p: SurfaceProps) {
  const { url, kind } = p;
  const emit = useEmit(p);
  const controller = useRef<LinkPlayerController | null>(null);
  const own = useRef(false);
  // The URL last loaded: events are reported against it (a superseded load's events never arrive).
  const loadedUrl = useRef<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [scriptFailed, setScriptFailed] = useState(false);
  const opts = useLatest({ autoPlay: p.autoPlay, startMs: p.startMs, providerControls: p.providerControls });

  const handle = useMemo<MediaHandle>(
    () => ({
      play: () => controller.current?.play(),
      pause: () => controller.current?.pause(),
      seek: (ms) => controller.current?.seekTo(ms),
      setRate: () => {},
      reload: () => {},
      sample: () => null,
    }),
    [],
  );
  useImperativeHandle(p.handleRef, () => handle, [handle]);

  const tap = useTapToListen(p, !!p.shouldPlay && loaded && !playing && !ended && !failed, handle.play, true);

  const onMount = (c: LinkPlayerController, api: LinkPlayerApi) => {
    controller.current = c;
    own.current = kind.caps.ownControls && opts.current.providerControls === true && api.CONTROLS === true;
    emit({ type: "controls", own: own.current });
    setMounted(true);
  };
  const onEvent = (e: LinkPlayerEvent) => {
    const key = loadedUrl.current ?? url;
    if (e.type === "loaded") {
      setLoaded(true);
      setFailed(false);
      emit({ type: "loaded", key, durationMs: e.durationMs });
    } else if (e.type === "state") {
      // Only "playing" is sound; a blocked YouTube autoplay sits in "buffering".
      setPlaying(e.state === "playing");
      setEnded(e.state === "ended");
      if (e.state === "playing") tap.setBlocked(false);
      emit({ type: "state", key, state: e.state, positionMs: e.positionMs });
      if (e.state === "ended") emit({ type: "ended", key });
    } else if (e.type === "position") {
      emit({ type: "position", key, positionMs: e.positionMs });
    } else if (e.type === "error") {
      setFailed(true);
      emit({ type: "error", key });
    }
  };
  const onScriptError = () => {
    setFailed(true);
    setScriptFailed(true);
  };
  const onDestroy = () => {
    controller.current = null;
    loadedUrl.current = null;
  };

  // Relies on the hosted player (verified in player.js): a superseded load never emits `loaded` and its state / error
  // events are suppressed (sequence check). New media → load at the start the screen gives.
  useEffect(() => {
    const c = controller.current;
    if (!mounted || !c || loadedUrl.current === url) return;
    loadedUrl.current = url;
    setLoaded(false);
    setPlaying(false);
    setEnded(false);
    setFailed(false);
    const { autoPlay, startMs } = opts.current;
    const play = typeof autoPlay === "function" ? autoPlay() : autoPlay === true;
    const start = { startMs: startMs?.() ?? 0, play };
    c.load(url, own.current ? { ...start, controls: true } : start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, url]);

  // Without the hosted player nothing loads: each new media fails at once (a caster skips it).
  useEffect(() => {
    if (scriptFailed) emit({ type: "error", key: url });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptFailed, url]);

  const host = (
    <EmbedPlayer
      testId={p.hostTestId}
      className={p.hostClassName ?? "w-full h-full"}
      onMount={onMount}
      onEvent={onEvent}
      onScriptError={onScriptError}
      onDestroy={onDestroy}
    />
  );
  return (
    <>
      <Frame frame={p.frame} media={host} overlays={<>{failed && p.showError && <ErrorOverlay />}{tap.overlay(failed)}</>} />
      {tap.below(failed)}
    </>
  );
}
